import type { ActionContext } from "../actions/types";
import type { TProduct, TGlobalProduct } from "../../data";

/**
 * Feature flag gating the feed-aware "View Live Chart" context-menu logic.
 *
 * When OFF, "View Live Chart" behaves exactly as before. When ON, live customer
 * curves resolve to either a direct "plot internal" action or a submenu of the
 * curve's mapped third-party feeds (see {@link resolveLiveChartFeeds}).
 *
 * Toggle per-environment via Hasura `feature_flags`, or override locally with:
 *   localStorage.setItem("feature-flag:live-chart-feed-selection", JSON.stringify({ metadata: {} }))
 */
export const LIVE_CHART_FEED_SELECTION_FLAG = "live-chart-feed-selection";

/** Tooltip shown when the curve type (delta/snapshot) can't be plotted on a live chart. */
export const LIVE_CHART_WRONG_TYPE_TOOLTIP = "This Curve type can't be plotted on a Live Chart";

/** Tooltip shown when the user lacks permission to any of the curve's mapped feeds. */
export const LIVE_CHART_NO_PERMISSION_TOOLTIP = "You don't have permission to plot this Curve on a Live Chart";

/** A single selectable feed in the "View Live Chart" submenu. */
export type LiveChartFeed = {
  /** Product id to open in the live chart (curve's own id for internal, else the global product id). */
  productId: string;
  /** Display label (source alias/name, or "Internal"). */
  label: string;
  /** `source` number used to render the feed's package icon (null for internal). */
  source: number | null;
  /** True for the org's own "Internal" (MYCO) data. */
  isInternal: boolean;
};

/** A curve's mapped third-party feed, resolved against the user's accessible global products. */
export type MappedFeed = {
  /** The `global_product` id (also the id used to chart this feed). */
  id: string;
  /** Display label (source alias/name, falling back to the feed name/id). */
  label: string;
  /** `source` number used to render the feed's package icon (null if the feed is inaccessible). */
  source: number | null;
  /** The user's permission on this feed (`null` when no access row exists). */
  permission: string | null;
  /** True when the user can actually plot this feed (has access and it isn't list-only). */
  permissioned: boolean;
};

/**
 * Resolves a curve's mapped third-party feeds (`product_global_dependencies`) against the
 * user's accessible global products, returning each feed's label, icon source, and permission.
 * Shared by {@link resolveLiveChartFeeds} and the Debug Panel's "Third Party Feeds" view.
 */
export function getMappedFeeds(
  product: Pick<TProduct, "product_global_dependencies">,
  globalProducts: readonly TGlobalProduct[] | null | undefined,
): MappedFeed[] {
  return (product.product_global_dependencies ?? []).map((dep) => {
    const gp = globalProducts?.find((g) => g.id === dep.global_product);
    const permission = gp?.globalPackageByGlobalPackage.global_permissions[0]?.permission ?? null;
    const src = gp?.globalPackageByGlobalPackage.sourceBySource;
    return {
      id: dep.global_product,
      label: src?.alias ?? src?.name ?? gp?.name ?? dep.global_product,
      source: gp?.globalPackageByGlobalPackage.source ?? null,
      permission,
      // "list" means visible-only (no access); a missing global product/permission means no access.
      permissioned: !!gp && !!permission && permission !== "list",
    };
  });
}

/**
 * Resolved state of the feed-aware "View Live Chart" item for the right-clicked curve.
 * - `passthrough`: flag off or not a curve column — use the existing (non-feed) behaviour.
 * - `plot-internal`: enabled, no submenu, plots the curve's own product directly.
 * - `disabled-type`: curve_type is not `live` (delta/snapshot).
 * - `disabled-permission`: curve has mapped feeds, the user is permissioned to none, and the
 *   org does not need historical data (so there's no internal option to fall back on either).
 * - `submenu`: show a submenu of the (permissioned) feeds.
 */
export type LiveChartFeedState =
  | { kind: "passthrough" }
  | { kind: "plot-internal" }
  | { kind: "disabled-type" }
  | { kind: "disabled-permission" }
  | { kind: "submenu"; feeds: LiveChartFeed[] };

/**
 * Pure decision function implementing the feed-aware "View Live Chart" flow.
 * Shared by the item's `disabled`, `tooltip`, `subMenu`, and `action` resolvers so
 * the branching lives in one place.
 */
export function resolveLiveChartFeeds(ctx: ActionContext): LiveChartFeedState {
  if (!ctx.liveChartFeedSelectionEnabled || !ctx.isProduct) return { kind: "passthrough" };

  const product = ctx.productId ? ctx.productsById?.[ctx.productId] : undefined;
  if (!product) return { kind: "passthrough" };

  // 1. Only live curves can be plotted on a live chart.
  if (product.curve_type !== "live") return { kind: "disabled-type" };

  // 2. Canned/sourced (exchange/system) curves keep the current behaviour — no submenu.
  if (product.artis_type === "canned" || product.artis_type === "sourced") {
    return { kind: "plot-internal" };
  }

  // 3. Resolve the curve's mapped third-party feeds against the user's global products.
  const mapped = getMappedFeeds(product, ctx.globalProducts);

  // No feeds mapped at all → plot the curve's own internal data directly, but only when the org has historical data. Without it there is no internal data to plot, so disable instead of plotting into a "Contract Expired" error.
  if (mapped.length === 0) {
    return ctx.orgNeedsHistoricalData ? { kind: "plot-internal" } : { kind: "disabled-permission" };
  }

  const permissionedFeeds: LiveChartFeed[] = mapped
    .filter((f) => f.permissioned)
    .map((f) => ({ productId: f.id, label: f.label, source: f.source, isInternal: false }));

  // 4/5. Prepend the internal (MYCO) option when the org needs historical data. This is available regardless of third-party feed permissions, so build it before the permission gate below.
  const feeds: LiveChartFeed[] = ctx.orgNeedsHistoricalData
    ? [{ productId: product.id, label: "Internal", source: null, isInternal: true }, ...permissionedFeeds]
    : permissionedFeeds;

  // Nothing to offer, no internal option and no accessible feeds, so we return disabled.
  if (feeds.length === 0) return { kind: "disabled-permission" };

  return { kind: "submenu", feeds };
}

export function liveChartHasValidOption(ctx: ActionContext): boolean {
  const state = resolveLiveChartFeeds(ctx);
  return state.kind === "submenu" || state.kind === "plot-internal";
}

export function shouldDisableLiveChartForCustomerCurve(
  artisType: string | undefined,
  orgNeedsHistoricalData: boolean,
): boolean {
  if (orgNeedsHistoricalData) return false;
  return artisType === "customer_curve";
}
